import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { useLocation, useNavigate } from 'react-router-dom';
import InputsService from '@api/Inputs/InputCtrl';
import { DistributionCenterInventory } from '@api/DistributionCenterInventories/DistributionCenterInventory';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import DistributionCenterInventoryService from '@api/DistributionCenterInventories/DistributionCenterInventoryCtrl';
import DistributionCenterInventoryHistoryService from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistoryCtrl';
import { DistributionCenterInventoryHistory } from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistory';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import SearchIcon from '@mui/icons-material/Search';

const InventoryAddInputsScreen: React.FunctionComponent = () => {
    const navigation = useNavigate();
    const location = useLocation();
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const inputsService = useMemo(() => new InputsService(), []);
    const distributionCenterInventoryService = useMemo(() => new DistributionCenterInventoryService(), []);
    const distributionCenterInventoryHistoryService = useMemo(
        () => new DistributionCenterInventoryHistoryService(),
        []
    );
    const [distributionCenterId, setDistributionCenterId] = useState<string | undefined>(undefined);
    const [inventory, setInventory] = useState<any[]>([]);

    const [filters, setFilters] = useState({
        search: ''
    });

    const getData = async (distributionCenterId: string) => {
        showLoader();
        try {
            const data = await inputsService.getInputs();
            const inventoryArray: DistributionCenterInventory[] = [];
            data.forEach((input) => {
                const isAvailableInAll = input.distributionCentersAvailable?.includes('all') ?? true;
                const isAvailableInThisCenter =
                    isAvailableInAll || input.distributionCentersAvailable?.includes(distributionCenterId);

                if (isAvailableInThisCenter && input.id) {
                    inventoryArray.push({
                        id: input.id,
                        distributionCenterId,
                        input,
                        stock: 0
                    });
                }
            });
            setDistributionCenterId(distributionCenterId);
            setInventory(inventoryArray);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onAddInputs = async () => {
        showLoader();
        try {
            const validItems = inventory.filter((i) => i.stock > 0);

            if (validItems.length === 0) {
                showAlertError('Agrega insumos');
                return;
            }

            if (!distributionCenterId || !loggedUser) {
                throw new Error('Falta información requerida');
            }

            const inventoryHistory: DistributionCenterInventoryHistory = {
                distributionCenterId,
                inputs: [],
                date: new Date(),
                userData: loggedUser,
                movementType: 'input'
            };

            for (const item of validItems) {
                await distributionCenterInventoryService.saveInventory({
                    distributionCenterId,
                    input: item.input,
                    stock: item.stock
                });
                inventoryHistory.inputs.push({
                    input: item.input,
                    quantity: item.stock
                });
            }

            await distributionCenterInventoryHistoryService.saveInventoryHistory(inventoryHistory);
            navigation(-1);
        } catch (error: any) {
            showAlertError(error);
        } finally {
            hideLoader();
        }
    };

    const onRemoveStock = useCallback(
        (id?: string) => () => {
            if (id) {
                const inventoryArray = [...inventory];
                const index = inventoryArray.findIndex((item) => item.id === id);
                if (index !== -1 && inventoryArray[index].stock > 0) {
                    inventoryArray[index].stock--;
                    setInventory(inventoryArray);
                }
            }
        },
        [inventory]
    );

    const onAddStock = useCallback(
        (id?: string) => () => {
            if (id) {
                const inventoryArray = [...inventory];
                const index = inventoryArray.findIndex((item) => item.id === id);
                if (index !== -1) {
                    inventoryArray[index].stock++;
                    setInventory(inventoryArray);
                }
            }
        },
        [inventory]
    );

    const getTotal = () => {
        return inventory.reduce((a, inventory) => a + inventory.stock * inventory.input.purchaseCost, 0).toFixed(2);
    };

    useEffect(() => {
        const state = location.state as { distributionCenterId: string } | undefined;
        const distributionCenterId = state?.distributionCenterId;
        if (distributionCenterId) {
            void getData(distributionCenterId);
        }
    }, [location.state]);

    const columns = useMemo<ResponsiveGridColDef<DistributionCenterInventory>[]>(
        () => [
            {
                field: 'name',
                flex: 3,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.input.name}</Typography>,
                valueGetter: ({ row }) => row.input.name
            },
            {
                field: 'stock',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Stock entrando
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton color="secondary" onClick={onRemoveStock(row.id)}>
                            <RemoveIcon />
                        </IconButton>
                        <Typography>{row.stock}</Typography>
                        <IconButton color="secondary" onClick={onAddStock(row.id)}>
                            <AddIcon />
                        </IconButton>
                    </Stack>
                ),
                valueGetter: ({ row }) => row.stock
            },
            {
                field: 'presentation',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Presentacíon
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>{`${row.input.presentation.value} ${row.input.presentation.unit}`}</Typography>
                ),
                valueGetter: ({ row }) => row.input.presentation.value
            },
            {
                field: 'cost',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo unitario
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost}`}</Typography>,
                valueGetter: ({ row }) => row.input.purchaseCost
            },
            {
                field: 'total',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost * row.stock}`}</Typography>
            }
        ],
        [onRemoveStock, onAddStock]
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%">
                <Box>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} />
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar categoría"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Stack direction="row">
                                <Button variant="contained" color="success" onClick={onAddInputs}>
                                    Registrar entradas
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Typography variant="h6" fontWeight={600}>
                                {`Total: $${getTotal()}`}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
                <ResponsiveDataGrid searchValue={filters.search} rows={inventory} columns={columns} />
            </Stack>
        </Box>
    );
};
export default InventoryAddInputsScreen;
